import React, { useEffect } from 'react';
import { useFiltersContext } from './FiltersContext';

interface IProps {
  filterName: string,
  wrappedComponent: React.ElementType,
  [key: string]: any,
}

const Filters = ({
  filterName,
  wrappedComponent: WrappedComponent,
  ...props
}: IProps) => {
  const {
    filters: filtersStore,
    filtersInitialize,
    filtersUpdate,
    filtersReset,
    filterByChange,
  } = useFiltersContext();

  useEffect(() => {
    filtersInitialize(filterName);

    return () => {
      filtersReset(filterName);
    };
  }, [filterName]);

  const isFilterChecked = (filterBy: string, filterId: string) => {
    if (filtersStore[filterName][filterBy]) {
      return filtersStore[filterName][filterBy].includes(filterId);
    }

    return false;
  };

  const handleFilterUpdate = (filters: { [key: string]: string[] }) => {
    filtersUpdate(filterName, filters);
  };

  const handleFilterByChange = (filterBy: string, filterIds: string[] = []) => {
    filterByChange(filterName, filterBy, filterIds);
  };

  const findActiveChildren = (children: any[] = [], parentIds: string[] = []) => {
    if (!children.length) {
      return [];
    }

    return children
      .filter((child) => ((child.supportCheckDisabled && child.disabled) ?
        true : !child.disabled) && !parentIds.includes(child.name))
      .map((child) => child.name);
  };

  const handleParentFilterChange = (
    checkbox: any,
    nestedName: string,
    parentIds: string[] = [],
  ) => {
    const { checked, children } = checkbox;

    if (children.length) {
      const ids = findActiveChildren(children, parentIds);
      toggleParentFilter(checked, nestedName, ids);
    }
  };

  const toggleParentFilter = (
    toggle: boolean,
    // @ts-ignore
    nestedName: string = null,
    nestedIds: string[] = [],
  ) => {
    if (toggle) {
      let nestedUnchecked = (nestedName in filtersStore[filterName]) ?
        nestedIds.filter((id) => !filtersStore[filterName][nestedName].includes(id)) :
        nestedIds;
      handleFilterByChange(nestedName, nestedUnchecked);
    } else {
      let nestedChecked = (nestedName in filtersStore[filterName]) ?
        filtersStore[filterName][nestedName].filter((id) => nestedIds.includes(id)) :
        nestedIds;
      handleFilterByChange(nestedName, nestedChecked);
    }
  };

  return (
    <WrappedComponent
      { ...props }
      filtersStore={ filtersStore }
      filtersInitialize={ filtersInitialize }
      filtersUpdate={ filtersUpdate }
      filtersReset={ filtersReset }
      filterByChange={ filterByChange }
      findActiveChildren={ findActiveChildren }
      isFilterChecked={ isFilterChecked }
      handleFilterUpdate={ handleFilterUpdate }
      handleParentFilterChange={ handleParentFilterChange }
      handleFilterByChange={ handleFilterByChange }
      toggleParentFilter={ toggleParentFilter }
    />
  );
};

const createFiltersStore = (
  filtersProps: Omit<IProps, 'wrappedComponent'>,
) => (WrappedComponent: IProps['wrappedComponent']) => {
  return (props) => (
    <Filters
      { ...filtersProps }
      { ...props }
      wrappedComponent={ WrappedComponent }
    />
  );
};

export default createFiltersStore;
